<script setup lang="ts">
  import { Search, CircleX, Menu2, ChevronDown } from '@vicons/tabler'

  type NavChild = { to: string; label: string }
  type NavItem = {
    to: string
    label: string
    exact?: boolean
    children?: NavChild[]
  }

  const route = useRoute()
  const { t, locale } = useI18n()
  const localePath = useLocalePath()
  const switchLocalePath = useSwitchLocalePath()

  const headerSolid = ref(false)
  const megaOpen = ref(false)
  const searchOpen = ref(false)
  const mobileMenuOpen = ref(false)
  const mobileExpanded = ref<string | null>(null)
  const searchKeyword = ref('')
  const hoveredNav = ref<string | null>(null)
  const searchInput = ref<HTMLInputElement | null>(null)
  let megaCloseTimer: ReturnType<typeof setTimeout> | null = null

  const panelOpen = computed(() => megaOpen.value || searchOpen.value)

  function goSearch() {
    const q = searchKeyword.value.trim()
    if (!q) return
    const base = localePath('/news')
    closePanel()
    closeMobileMenu()
    navigateTo({ path: base, query: { q } })
  }

  const hasHero = computed(() => Boolean(route.meta.hasHero))

  const otherLocale = computed(() => (locale.value === 'zh' ? 'en' : 'zh'))

  const navItems = computed<NavItem[]>(() => [
    { to: localePath('/'), label: t('nav.home'), exact: true },
    {
      to: localePath('/about'),
      label: t('nav.about'),
      children: [
        { to: localePath('/about') + '#intro', label: t('nav.aboutIntro') },
        { to: localePath('/about') + '#philosophy', label: t('nav.aboutPhilosophy') },
        { to: localePath('/about') + '#campus', label: t('nav.aboutCampus') },
      ],
    },
    {
      to: localePath('/programs'),
      label: t('nav.programs'),
      children: [
        { to: localePath('/programs') + '#ad', label: t('nav.programsAd') },
        { to: localePath('/programs') + '#ic', label: t('nav.programsIc') },
        { to: localePath('/programs') + '#curriculum', label: t('nav.programsCurriculum') },
      ],
    },
    {
      to: localePath('/education'),
      label: t('nav.education'),
      children: [
        { to: localePath('/education') + '#pbl', label: t('nav.educationPbl') },
        { to: localePath('/education') + '#mentor', label: t('nav.educationMentor') },
        { to: localePath('/education') + '#badge', label: t('nav.educationBadge') },
      ],
    },
    { to: localePath('/faculty'), label: t('nav.faculty') },
    // { to: localePath('/research'), label: t('nav.research') },
    { to: localePath('/admission'), label: t('nav.admission') },
    { to: localePath('/contact'), label: t('nav.contact') },
  ])

  function isActive(item: NavItem) {
    const path = route.path.replace(/\/$/, '') || '/'
    const target = item.to.replace(/\/$/, '') || '/'
    if (item.exact) return path === target
    return path === target || path.startsWith(`${target}/`)
  }

  function isMobileHeader() {
    return window.matchMedia('(max-width: 760px)').matches
  }

  function updateHeader() {
    if (!hasHero.value) {
      headerSolid.value = true
      return
    }
    // 手机端始终实心底栏，避免透明态白图标在亮色 Hero 上看不见
    if (isMobileHeader()) {
      headerSolid.value = true
      return
    }
    headerSolid.value = window.scrollY > 350
  }

  function openMega(item: NavItem) {
    if (searchOpen.value || mobileMenuOpen.value) return
    if (!item.children?.length) {
      closeMega()
      return
    }
    if (megaCloseTimer) {
      clearTimeout(megaCloseTimer)
      megaCloseTimer = null
    }
    hoveredNav.value = item.to
    megaOpen.value = true
  }

  function openSearch() {
    if (megaCloseTimer) {
      clearTimeout(megaCloseTimer)
      megaCloseTimer = null
    }
    megaOpen.value = false
    hoveredNav.value = null
    closeMobileMenu()
    searchOpen.value = true
    nextTick(() => searchInput.value?.focus())
  }

  function closeSearch() {
    searchOpen.value = false
  }

  function toggleMobileMenu() {
    if (mobileMenuOpen.value) {
      closeMobileMenu()
      return
    }
    closeMega()
    closeSearch()
    mobileMenuOpen.value = true
  }

  function closeMobileMenu() {
    mobileMenuOpen.value = false
    mobileExpanded.value = null
  }

  function toggleMobileExpand(item: NavItem) {
    if (!item.children?.length) return
    mobileExpanded.value = mobileExpanded.value === item.to ? null : item.to
  }

  function scheduleCloseMega() {
    if (searchOpen.value || mobileMenuOpen.value) return
    if (megaCloseTimer) clearTimeout(megaCloseTimer)
    megaCloseTimer = setTimeout(() => {
      megaOpen.value = false
      hoveredNav.value = null
      megaCloseTimer = null
    }, 120)
  }

  function closeMega() {
    if (megaCloseTimer) {
      clearTimeout(megaCloseTimer)
      megaCloseTimer = null
    }
    megaOpen.value = false
    hoveredNav.value = null
  }

  function closePanel() {
    closeMega()
    closeSearch()
  }

  watch(
    () => route.fullPath,
    () => {
      closePanel()
      closeMobileMenu()
      nextTick(updateHeader)
    },
  )

  onMounted(() => {
    updateHeader()
    window.addEventListener('scroll', updateHeader, { passive: true })
    window.addEventListener('resize', updateHeader, { passive: true })
    onUnmounted(() => {
      window.removeEventListener('scroll', updateHeader)
      window.removeEventListener('resize', updateHeader)
      if (megaCloseTimer) clearTimeout(megaCloseTimer)
    })
  })
</script>

<template>
  <div
    id="siteHeader"
    class="site-header"
    :class="{
      solid: headerSolid && !panelOpen,
      transparent: !headerSolid || panelOpen,
      'mega-open': panelOpen,
      'nav-open': megaOpen,
      'search-open': searchOpen,
      'mobile-open': mobileMenuOpen,
    }"
    @mouseleave="scheduleCloseMega"
  >
    <div class="mega-backdrop" aria-hidden="true" />

    <div class="header-panel">
      <div class="utility-bar">
        <a href="https://www.xinwei-ai.com/" target="_blank" rel="noopener">{{ t('header.xinwei') }}</a>
        <a href="https://www.sanyau.edu.cn/" target="_blank" rel="noopener">{{ t('header.sanyau') }}</a>

        <NuxtLink :to="switchLocalePath(otherLocale)">{{ t('header.langSwitch') }}</NuxtLink>
        <button class="search" type="button" @click="openSearch">
          <Search class="header-icon" aria-hidden="true" />
          <span>{{ t('header.search') }}</span>
        </button>
      </div>

      <div class="header-main">
        <div class="logo-container">
          <NuxtLink :to="localePath('/')">
            <img
              class="logo-img"
              :src="headerSolid && !panelOpen ? '/images/home_logo_solid.png' : '/images/home_logo.png'"
              :alt="`${t('brand.name')} ${t('brand.nameEn')}`"
            />
          </NuxtLink>
        </div>

        <nav class="main-nav">
          <div
            v-for="item in navItems"
            :key="item.to"
            class="nav-item"
            :class="{
              active: isActive(item),
              hover: hoveredNav === item.to,
              'has-children': Boolean(item.children?.length),
            }"
            @mouseenter="openMega(item)"
          >
            <NuxtLink class="nav-link" :to="item.to">{{ item.label }}</NuxtLink>
            <div v-if="item.children?.length" class="nav-dropdown">
              <NuxtLink
                v-for="child in item.children"
                :key="child.to"
                class="nav-dropdown-link"
                :to="child.to"
                @click="closePanel"
              >
                {{ child.label }}
              </NuxtLink>
            </div>
          </div>
        </nav>
      </div>
    </div>

    <div class="mobile-actions">
      <button class="mobile-search-btn" type="button" aria-label="搜索" @click="openSearch">
        <Search class="header-icon header-icon--22" aria-hidden="true" />
      </button>
      <button
        class="mobile-menu-btn"
        type="button"
        :aria-expanded="mobileMenuOpen"
        aria-label="菜单"
        @click="toggleMobileMenu"
      >
        <CircleX v-if="mobileMenuOpen" class="header-icon header-icon--26" aria-hidden="true" />
        <Menu2 v-else class="header-icon header-icon--26" aria-hidden="true" />
      </button>
    </div>

    <div class="search-panel">
      <form class="search-form" @submit.prevent="goSearch">
        <Search class="search-form-icon header-icon header-icon--22" aria-hidden="true" />
        <input
          ref="searchInput"
          v-model="searchKeyword"
          type="search"
          :placeholder="t('header.searchPlaceholder')"
          autocomplete="off"
        />
        <button type="submit" :disabled="!searchKeyword.trim()">{{ t('header.search') }}</button>
      </form>
    </div>

    <button v-show="searchOpen" class="search-close" type="button" aria-label="关闭" @click="closeSearch">
      <CircleX class="header-icon header-icon--36" aria-hidden="true" />
    </button>

    <div class="mobile-drawer-mask" :class="{ open: mobileMenuOpen }" @click="closeMobileMenu" />
    <aside class="mobile-drawer" :class="{ open: mobileMenuOpen }" :aria-hidden="!mobileMenuOpen">
      <div class="mobile-drawer-head">
        <NuxtLink :to="switchLocalePath(otherLocale)" class="mobile-lang" @click="closeMobileMenu">
          {{ t('header.langSwitch') }}
        </NuxtLink>
        <button type="button" class="mobile-drawer-close" aria-label="关闭菜单" @click="closeMobileMenu">
          <CircleX class="header-icon header-icon--28" aria-hidden="true" />
        </button>
      </div>
      <nav class="mobile-nav">
        <div
          v-for="item in navItems"
          :key="item.to"
          class="mobile-nav-item"
          :class="{
            active: isActive(item),
            expanded: mobileExpanded === item.to,
          }"
        >
          <div class="mobile-nav-row">
            <NuxtLink class="mobile-nav-link" :to="item.to" @click="closeMobileMenu">
              {{ item.label }}
            </NuxtLink>
            <button
              v-if="item.children?.length"
              type="button"
              class="mobile-nav-toggle"
              :aria-expanded="mobileExpanded === item.to"
              @click="toggleMobileExpand(item)"
            >
              <ChevronDown class="header-icon header-icon--18" aria-hidden="true" />
            </button>
          </div>
          <div v-if="item.children?.length" class="mobile-subnav">
            <NuxtLink
              v-for="child in item.children"
              :key="child.to"
              class="mobile-subnav-link"
              :to="child.to"
              @click="closeMobileMenu"
            >
              {{ child.label }}
            </NuxtLink>
          </div>
        </div>
      </nav>
      <div class="mobile-drawer-links">
        <a href="https://www.xinwei-ai.com/" target="_blank" rel="noopener">{{ t('header.xinwei') }}</a>
        <a href="https://www.sanyau.edu.cn/" target="_blank" rel="noopener">{{ t('header.sanyau') }}</a>
        <a href="#">{{ t('header.infoPublic') }}</a>
      </div>
    </aside>
  </div>
</template>

<style scoped lang="scss">
  .header-icon {
    display: block;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .header-icon--18 {
    width: 18px;
    height: 18px;
  }

  .header-icon--22 {
    width: 22px;
    height: 22px;
  }

  .header-icon--26 {
    width: 26px;
    height: 26px;
  }

  .header-icon--28 {
    width: 28px;
    height: 28px;
  }

  .header-icon--36 {
    width: 36px;
    height: 36px;
  }

  .site-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    height: 150px;
    overflow: visible;
    transition: background 0.4s ease, box-shadow 0.4s ease;
  }

  .site-header.transparent {
    background: transparent;
  }

  .site-header.solid {
    background: var(--white);
    box-shadow: var(--shadow-md);
  }

  .mega-backdrop {
    position: absolute;
    inset: 0 auto auto 0;
    width: 100%;
    height: 380px;
    background: rgba(12, 32, 64, 0.52);
    /* -webkit 必须写在标准属性之前：生产压缩会合并同源属性并只保留最后一个；
       若只剩 -webkit-，Chrome/Edge/Firefox 会丢掉虚化，看起来只剩很透的遮罩 */
    -webkit-backdrop-filter: blur(28px) saturate(1.2);
    backdrop-filter: blur(28px) saturate(1.2);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.4s ease, visibility 0.4s ease;
    z-index: 0;
  }

  .site-header.mega-open .mega-backdrop {
    opacity: 1;
    visibility: visible;
    /* 展开时接收鼠标，移出整块虚化区才关闭；导航/工具栏 z-index 更高仍可点击 */
    pointer-events: auto;
  }

  .logo-container {
    position: relative;
    z-index: 3;
    flex-shrink: 0;
    top: -25px;
  }

  .logo-img {
    width: clamp(180px, 28vw, 400px);
    height: clamp(42px, 6.2vw, 90px);
    object-fit: contain;
    display: block;
    transition: width 0.2s ease, height 0.2s ease;
  }

  .header-panel {
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;
    padding: 0 clamp(16px, 6vw, 100px);
  }

  .header-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: clamp(16px, 2vw, 40px);
    width: 100%;
    // margin-top: clamp(10px, 1.2vw, 18px);
  }

  .site-header.search-open .utility-bar,
  .site-header.search-open .main-nav {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }

  .utility-bar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    align-self: flex-end;
    gap: clamp(12px, 2.2vw, 40px);
    height: 58px;
    transition: opacity 0.3s ease, visibility 0.3s ease;

    a {
      color: #fff;
      font-size: clamp(14px, 0.9vw, 15px);
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 0.85;
      }
    }
  }

  .site-header.solid .utility-bar a {
    color: #333;
  }

  .site-header.mega-open .utility-bar a {
    color: #fff;
  }

  .search {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
    border: none;
    padding: 6px 18px;
    border-radius: 50px;
    cursor: pointer;
    font-size: 15px;
    font-family: inherit;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .site-header.solid .search {
    color: #333;
    background: rgba(0, 0, 0, 0.06);
  }

  .site-header.mega-open .search {
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
  }

  .site-header.search-open .search {
    background: rgba(255, 255, 255, 0.22);
  }

  .mobile-actions {
    display: none;
    position: absolute;
    top: 18px;
    right: 16px;
    z-index: 4;
    align-items: center;
    gap: 8px;
  }

  .mobile-search-btn,
  .mobile-menu-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border: none;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    cursor: pointer;
  }

  .site-header.solid .mobile-search-btn,
  .site-header.solid .mobile-menu-btn {
    background: rgba(0, 0, 0, 0.06);
    color: #333;
  }

  .site-header.search-open .mobile-actions {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }

  .search-panel {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 480px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.35s ease, visibility 0.35s ease;
  }

  .site-header.search-open .search-panel {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }

  .search-close {
    position: absolute;
    top: 28px;
    right: 40px;
    z-index: 5;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    padding: 4px;
    transition: color 0.2s ease, transform 0.2s ease;

    &:hover {
      color: #fff;
      transform: scale(1.06);
    }
  }

  .search-form {
    display: flex;
    align-items: center;
    width: min(680px, 82vw);
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.28);
    border-radius: 999px;
    padding: 6px 6px 6px 22px;
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
    transform: translateY(12px);
    transition: transform 0.35s ease;
  }

  .site-header.search-open .search-form {
    transform: translateY(0);
  }

  .search-form-icon {
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.85);
  }

  .search-form input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: #fff;
    font-size: 18px;
    font-family: inherit;
    padding: 12px 16px;
  }

  .search-form input::placeholder {
    color: rgba(255, 255, 255, 0.55);
  }

  .search-form input::-webkit-search-cancel-button {
    -webkit-appearance: none;
  }

  .search-form button {
    flex-shrink: 0;
    border: none;
    border-radius: 999px;
    background: #fff;
    color: #123;
    font-size: 15px;
    font-family: inherit;
    padding: 12px 28px;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover:not(:disabled) {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  .main-nav {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    flex: 1;
    min-width: 0;
    gap: clamp(14px, 2.8vw, 60px);
    margin-top: 0;
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }

  .nav-item {
    position: relative;
    padding-bottom: 12px;
  }

  .nav-link {
    position: relative;
    display: inline-block;
    color: #fff;
    font-size: clamp(17px, 1.05vw, 22px);
    line-height: 1.4;
    white-space: nowrap;
    transition: opacity 0.2s ease, font-size 0.2s ease;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -10px;
      height: 2px;
      background: #fff;
      transform: scaleX(0);
      transform-origin: center;
      transition: transform 0.25s ease;
    }

    &:hover {
      opacity: 0.9;
    }
  }

  .site-header.solid .nav-link {
    color: #333;

    &::after {
      background: var(--theme-blue);
    }
  }

  .site-header.mega-open .nav-link {
    color: #fff;

    &::after {
      background: #fff;
    }
  }

  .nav-item.hover > .nav-link::after {
    transform: scaleX(1);
  }

  .nav-dropdown {
    position: absolute;
    top: calc(100% + 20px);
    left: 0;
    display: flex;
    flex-direction: column;
    gap: 30px;
    min-width: max-content;
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transform: translateY(10px);
    transition: opacity 0.35s ease 0.05s, visibility 0.35s ease 0.05s, transform 0.35s ease 0.05s;
  }

  /* 移入某个父级时显示所有子级 */
  .site-header.nav-open .nav-dropdown {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
  }

  /* 悬停某个父级时只显示对应子菜单 */
  /* .nav-item.hover > .nav-dropdown {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transform: translateY(0);
  } */

  .nav-dropdown-link {
    color: rgba(255, 255, 255, 0.92);
    font-size: clamp(14px, 0.9vw, 15px);
    line-height: 1.4;
    white-space: nowrap;
    transition: opacity 0.2s ease, color 0.2s ease;

    &:hover {
      color: #fff;
      opacity: 0.8;
    }
  }

  .mobile-drawer-mask {
    position: fixed;
    inset: 0;
    z-index: 40;
    background: rgba(0, 0, 0, 0.45);
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }

  .mobile-drawer-mask.open {
    opacity: 1;
    visibility: visible;
  }

  .mobile-drawer {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 50;
    width: min(320px, 86vw);
    height: 100vh;
    background: #0f2848;
    color: #fff;
    box-shadow: -8px 0 30px rgba(0, 0, 0, 0.25);
    transform: translateX(105%);
    transition: transform 0.32s ease;
    display: flex;
    flex-direction: column;
    padding: 18px 18px 28px;
    overflow-y: auto;
  }

  .mobile-drawer.open {
    transform: translateX(0);
  }

  .mobile-drawer-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 18px;
  }

  .mobile-lang {
    color: rgba(255, 255, 255, 0.9);
    font-size: 15px;
  }

  .mobile-drawer-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    color: #fff;
    cursor: pointer;
    padding: 4px;
  }

  .mobile-nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
  }

  .mobile-nav-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .mobile-nav-link {
    flex: 1;
    color: #fff;
    font-size: clamp(16px, 4vw, 17px);
    padding: 12px 8px;
    line-height: 1.35;
  }

  .mobile-nav-item.active > .mobile-nav-row .mobile-nav-link {
    color: #7eb6ff;
  }

  .mobile-nav-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: transparent;
    color: rgba(255, 255, 255, 0.85);
    cursor: pointer;
    transition: transform 0.25s ease;
  }

  .mobile-nav-item.expanded .mobile-nav-toggle {
    transform: rotate(180deg);
  }

  .mobile-subnav {
    display: none;
    flex-direction: column;
    padding: 0 8px 8px 18px;
    gap: 2px;
  }

  .mobile-nav-item.expanded .mobile-subnav {
    display: flex;
  }

  .mobile-subnav-link {
    color: rgba(255, 255, 255, 0.78);
    font-size: clamp(14px, 3.5vw, 15px);
    padding: 10px 8px;
  }

  .mobile-drawer-links {
    min-height: 150px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.12);

    a {
      color: rgba(255, 255, 255, 0.72);
      font-size: 14px;
    }
  }

  @media (max-width: 1440px) {
    .logo-img {
      width: clamp(220px, 26vw, 340px);
      height: clamp(52px, 5.5vw, 78px);
    }

    .nav-link {
      font-size: clamp(16px, 1.05vw, 18px);
    }
  }

  @media (max-width: 1280px) {
    .header-panel {
      padding: 0 clamp(20px, 3vw, 40px);
    }

    .logo-img {
      width: clamp(200px, 24vw, 300px);
      height: clamp(48px, 5vw, 70px);
    }

    .main-nav {
      gap: clamp(12px, 1.8vw, 28px);
    }

    .nav-link {
      font-size: clamp(15px, 1.05vw, 17px);
    }

    .utility-bar {
      gap: clamp(12px, 1.8vw, 24px);
    }
  }

  @media (max-width: 1024px) {
    .site-header {
      height: clamp(120px, 16vw, 160px);
    }

    .logo-img {
      width: clamp(180px, 28vw, 260px);
      height: clamp(44px, 5.5vw, 60px);
    }

    .nav-link {
      font-size: 15px;
    }

    .nav-dropdown {
      gap: 18px;
      top: calc(100% + 12px);
    }

    .utility-bar a,
    .search {
      font-size: 14px;
    }
  }

  @media (max-width: 760px) {
    .site-header {
      height: 72px;
      width: 100%;
      max-width: 100vw;
      overflow: visible;
    }

    .header-panel {
      height: 100%;
      padding: 0 14px;
      padding-right: 108px; /* 给右侧搜索/菜单留位 */
      justify-content: center;
      box-sizing: border-box;
      max-width: 100%;
    }

    .utility-bar,
    .main-nav {
      display: none;
    }

    .logo-container {
      top: 0;
    }

    .header-main {
      margin-top: 0;
      min-height: 72px;
      align-items: center;
      width: 100%;
      max-width: 100%;
    }

    .logo-img {
      width: min(180px, 52vw);
      height: 40px;
    }

    .mobile-actions {
      display: flex;
      top: 50%;
      right: 12px;
      transform: translateY(-50%);
    }

    .mobile-search-btn,
    .mobile-menu-btn {
      width: 40px;
      height: 40px;
      background: rgba(0, 0, 0, 0.06);
      color: #333;
    }

    .mega-backdrop {
      position: fixed;
      inset: 0;
      width: 100%;
      max-width: 100vw;
      height: 100%;
    }

    .site-header.search-open .logo-container {
      z-index: 7;
    }

    .search-panel {
      position: fixed;
      inset: 0;
      width: 100%;
      max-width: 100vw;
      height: 100%;
      padding: 0 16px;
      box-sizing: border-box;
      align-items: center;
      justify-content: center;
    }

    .search-close {
      top: 16px;
      right: 16px;
      z-index: 8;
    }

    .search-form {
      width: 100%;
      max-width: calc(100vw - 32px);
      box-sizing: border-box;
      flex-wrap: nowrap;
      border-radius: 999px;
      padding: 6px 6px 6px 14px;
      gap: 0;
    }

    .search-form input {
      flex: 1;
      min-width: 0;
      font-size: 15px;
      padding: 10px 8px;
    }

    .search-form button {
      flex-shrink: 0;
      width: auto;
      padding: 10px 16px;
      font-size: 14px;
      border-radius: 999px;
    }
  }

  @media (max-width: 420px) {
    .search-form {
      flex-direction: column;
      flex-wrap: nowrap;
      border-radius: 16px;
      padding: 10px;
      gap: 8px;
      align-items: stretch;
    }

    .search-form-icon {
      display: none;
    }

    .search-form input {
      width: 100%;
      flex: none;
      padding: 8px 4px;
      box-sizing: border-box;
    }

    .search-form button {
      width: 100%;
      border-radius: 12px;
      box-sizing: border-box;
    }
  }
</style>
